import {
  Box,
  Button,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MagnifyingGlass, Plus } from '@phosphor-icons/react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../../hooks/useDarkMode';
import useFlowsStore from '../../../store/storeFlows';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { DashboardSelector } from './DashboardSelector';
import { SaveStatusButton } from './TopBar/SaveStatusButton';
import { SearchDropdown } from './TopBar/SearchDropdown';
import { SearchResult, useSearch } from './TopBar/useSearch';

const SearchTextField = styled(TextField)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    width: '100%',
    backgroundColor: 'transparent',
    '& .MuiOutlinedInput-root': {
      height: '33px',
      borderRadius: '4px',
      backgroundColor: isDarkMode ? palette.gray[800] : palette.gray.A100,
      '& fieldset': {
        borderColor: isDarkMode ? palette.gray[700] : palette.neutral[200],
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? palette.gray[600] : palette.neutral[200],
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? palette.gray[500] : palette.blue[500],
      },
    },
    '& input': {
      fontFamily: 'Roboto',
      fontSize: '14px',
      color: isDarkMode ? palette.gray[200] : palette.gray[700],
      '&::placeholder': {
        color: isDarkMode ? palette.gray[500] : palette.gray[400],
        opacity: 1,
      },
    },
  };
});

const AddWidgetButton = styled(Button)(({ theme }) => {
  const palette = theme.customColorPalette;
  return {
    height: '32px',
    marginLeft: '16px',
    borderRadius: '4px',
    backgroundColor: palette.blue[500],
    textTransform: 'none',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 500,
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingTop: '4px',
    paddingBottom: '4px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: palette.blue[600],
      boxShadow: 'none',
    },
  };
});

interface TopBarProps {
  onAddWidget?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onAddWidget }) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const navigate = useNavigate();
  const saving = useFlowsStore(state => state.saving);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [status, setStatus] = useState({
    isOnline: true,
  });
  const [searchValue, setSearchValue] = useState('');
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const { searchResults, isSearchLoading } = useSearch(searchValue);

  useEffect(() => {
    const handleOnline = () => setStatus(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setStatus(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    setStatus(prev => ({ ...prev, ...saving }));

    if (!saving.finished && saving.status === 'error') {
      setNotification({
        type: NotificationType.Error,
        message:
          'Could not save your latest changes. Reload to recover the last saved version.',
        persist: true,
        key: 'autosave-error',
        action: { label: 'Reload', onClick: () => window.location.reload() },
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [saving, setNotification]);

  const handleSearchResultClick = useCallback(
    (result: SearchResult) => {
      setSearchValue('');
      setSearchAnchorEl(null);
      if (result.isForm) {
        navigate(`/dashboard-beta/interface/${result.id}`);
      } else {
        navigate(`/dashboard-beta/agent/${result.id}`);
      }
    },
    [navigate],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (e.target.value.trim() && searchInputRef.current) {
      setSearchAnchorEl(searchInputRef.current);
    } else {
      setSearchAnchorEl(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
        borderBottom: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]
          }`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '48px',
          paddingX: '16px',
          gap: '16px',
        }}
      >
        <DashboardSelector />

        <Box sx={{ position: 'relative', flex: 1, maxWidth: '320px' }}>
          <SearchTextField
            inputRef={searchInputRef}
            size='small'
            placeholder='Search agents, interfaces, chats...'
            value={searchValue}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <MagnifyingGlass
                    size={16}
                    color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                  />
                </InputAdornment>
              ),
            }}
          />

          <SearchDropdown
            anchorEl={searchAnchorEl}
            searchValue={searchValue}
            searchResults={searchResults}
            isSearchLoading={isSearchLoading}
            isDarkMode={isDarkMode}
            palette={palette}
            onResultClick={handleSearchResultClick}
            resultsRef={resultsRef}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <SaveStatusButton
            saving={saving}
            isOnline={status.isOnline}
            isDarkMode={isDarkMode}
            palette={palette}
          />

          <AddWidgetButton
            variant='contained'
            startIcon={<Plus size={16} weight='bold' />}
            onClick={onAddWidget}
          >
            Add Widget
          </AddWidgetButton>
        </Box>
      </Box>
    </Box>
  );
};
